import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import Button from "designSystem/components/button/Button";
import { useLocale } from "designSystem/locale";
import PaneHeader from "designSystem/components/pane/PaneHeader";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import SearchTextField from "designSystem/components/textfield/SearchTextField";
import React from "react";
import { useErrorBoundary } from "react-error-boundary";
import { HeaderCallbacks, SelectedChannel } from "../../types";
import * as api from "../api";
import ChannelsList from "./ChannelsList";
import LoadingSlack from "./LoadingSlack";


const PREFIX = "ChannelSelection";

const classes = {
	root: `${PREFIX}root`,
	form: `${PREFIX}form`,
	search: `${PREFIX}search`,
	add: `${PREFIX}add`,
	list: `${PREFIX}list`,
	action: `${PREFIX}action`,
};

const StyledGrid = styled(Grid)(({
	theme,
}) => ({
	[`&.${classes.root}`]: {
		alignItems: "stretch",
		flexWrap: "nowrap",
		height: "100%",
		justifyContent: "flex-start",
		width: "100%",
	},

	[`& .${classes.form}`]: {
		alignItems: "stretch",
		flex: 1,
		minHeight: 0,
		height: "100%",
		justifyContent: "flex-start",
		width: "100%",
	},

	[`& .${classes.search}`]: {
		borderBottom: STANDARD_BORDER_STYLE,
		padding: theme.spacing(2, 3),
		width: "100%",
	},

	[`& .${classes.add}`]: {
		padding: theme.spacing(2),
		width: "100%",
	},

	[`& .${classes.list}`]: {
		flex: 1,
		minHeight: 0,
		height: "100%",
		overflowY: "scroll",
		width: "100%",
	},

	[`& .${classes.action}`]: {
		borderTop: STANDARD_BORDER_STYLE,
		padding: theme.spacing(2, 3),
		width: "100%",
	},
}));

interface Props extends HeaderCallbacks {
	onAddNewChannel: () => void;
	onSelectChannel: (channel: SelectedChannel) => void;
}

const ChannelSelection: React.FunctionComponent<Props> = ({ onBack, onClose, onAddNewChannel, onSelectChannel }) => {
	const locale = useLocale();
	const [selected, setSelected] = React.useState<SelectedChannel>();
	const [conversationList, setConversationList] = React.useState<api.ConversationList>();
	const [channelFilter, setChannelFilter] = React.useState<string>();
	const { showBoundary } = useErrorBoundary();


	React.useEffect(() => {
		async function fetchChannels() {
			try {
				const channelsJson = await api.getListOfChannels();
				setConversationList(channelsJson);
			} catch (error) {
				showBoundary(error);
			}
		}
		fetchChannels();
	}, []);
	const channels = (conversationList?.channels ?? []).filter((c) => !channelFilter || c.name.includes(channelFilter));
	return (
		<StyledGrid
			className={classes.root}
			container
			direction="column"
		>
			<PaneHeader
				back
				onBack={onBack}
				onClose={onClose}
				titleRes="SelectChannel"
			/>
			{
				null == conversationList
					? <LoadingSlack />
					: (
						<React.Fragment>
							<Grid
								className={classes.form}
								container
								direction="column"
								item
							>
								<Box className={classes.search}>
									<SearchTextField
										fullWidth
										onChange={(e) => setChannelFilter(e.target.value)}
										placeholder={locale.resource("SearchByChannelName")}
										value={channelFilter}
									/>
								</Box>
								<Box className={classes.add}>
									<Button
										design="link"
										onClick={onAddNewChannel}
										res="AddNewChannelTitle"
										startIcon={<AddCircleIcon />}
									/>
								</Box>
								<Box className={classes.list}>
									<ChannelsList
										channels={channels}
										onSelectChannel={setSelected}
										selected={selected}
									/>
								</Box>

							</Grid>
							<Grid className={classes.action} item>
								<Button
									design="primary"
									disabled={null == selected}
									fullWidth
									onClick={() => onSelectChannel(selected)}
									res="Connect"
								/>
							</Grid>
						</React.Fragment>
					)
			}
		</StyledGrid>
	);
};

export default ChannelSelection;